import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { Helmet } from "react-helmet";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import {
  clearRequest,
  customValidation,
  LoacalVariables,
  setLocalValues,
  validations,
} from "../../commonFunctions/CommonFunctions";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import logo from "../../images/4 sigma_logo.svg";
import backImg from "../../images/bg-4.png";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useAuth } from "../../StateManagement/authContext";
import { ipUrl } from "../../Config/url";

// Styled Components
const Background = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  backgroundImage: `url(${backImg})`, // Correct format
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: 380,
  textAlign: "center",
  borderRadius: "16px",
  boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.08)",

  border: "2px solid transparent", // Transparent so gradient shows
  background:
    "linear-gradient(213.05deg, #E1FFEB 0.19%, #EFFFFF 101.61%) padding-box, " + // inner background
    "linear-gradient(213.45deg, #4DD26B -5.18%, #C7F1D0 26.13%, #FFFFFF 49.27%, #ACE5E6 69.69%, #08B1B6 103.72%) border-box", // border gradient
}));


const LoginForm: React.FC = React.memo(() => {
  const { control, handleSubmit } = useForm();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const { setIsAuthenticated, setUser } = useAuth();

  const [loading, setLoading] = useState(false);
  // const [rememberMe, setRememberMe] = useState(false);

  const handleData = async (data: any) => {
    try {
      const request = {
        mobile: data.mobile,
        password: data.password,
        rememberMe: data.rememberMe,
      };
      const newRequest = clearRequest(request);
      setLoading(true);

      const response: any = await axios.post(
        `${ipUrl}/api/auth/login`,
        newRequest,
        { withCredentials: true }
      );

      if (response.data.success) {
        const user = response.data.user;
        setUser(user);
        setIsAuthenticated(true);

        setLocalValues(LoacalVariables.UserId, user.userId);
        setLocalValues(LoacalVariables.UserType, user.userType);
        setLocalValues(LoacalVariables.Name, user.name);

        //user datas;
        setLocalValues(LoacalVariables.Cds, user.cds);
        setLocalValues(LoacalVariables.Block, user.block);
        setLocalValues(LoacalVariables.District, user.district);

        showMessage(response.data.message, "success");
        navigate("/");
      }
    } catch (error: any) {
      console.error("Failed to login", error);
      if (error.message === "GlobalHandledError") return;
      showMessage(
        error?.response?.data.message || "Login Failed (something went wrong)",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | Willsoar Technologies</title>
        <meta
          name="description"
          content="Log in to access your estimates, invoices, clients, and more with our powerful estimation tool."
        />
      </Helmet>

      <Background>
        <LoginCard>
          <img
            src={logo}
            alt="Willsoar Logo"
            style={{ height: 70, width: 70, marginBottom: 13 }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              background:
                "linear-gradient(90.92deg, #4AD16E 0.42%, #08B1B6 99.56%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Welcome Back
          </Typography>

          <Typography variant="body2" color="#666666" sx={{ mb: 3 }}>
            Log in to your account to continue
          </Typography>

          <form onSubmit={handleSubmit(handleData)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <WSTextField
                  id="mobile"
                  label="Mobile Number"
                  type={TextType.AllowOnlyNumber}
                  name="mobile"
                  control={control}
                  rules={customValidation(true, validations.phone)}
                  minLength={10}
                  maxLength={10}
                  textIcon="Call"
                />
              </Grid>

              <Grid item xs={12}>
                <WSTextField
                  id="password"
                  label="Password"
                  type={TextType.Password}
                  name="password"
                  control={control}
                  rules={customValidation(true, validations.password)}
                  textIcon="Password"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  sx={{
                    py: 1.3,
                    fontWeight: 600,
                    borderRadius: "999px",
                    background:
                      "linear-gradient(90deg, #4AD16E 0%, #08B1B6 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(38, 132, 255, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, #4AD16E 0%, #08B1B6 100%)",
                    },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "Login"
                  )}
                </Button>
              </Grid>

              {/* <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  fontSize={12}
                >
                  <Link
                    to={"/forgot/password"}
                    style={{ color: "#666", textDecoration: "none" }}
                  >
                    Forgot Password?
                  </Link>
                  <Link
                    to={"/registration"}
                    style={{ color: "#666", textDecoration: "none" }}
                  >
                    Create Account
                  </Link>
                </Box>
              </Grid> */}
            </Grid>
          </form>

          <Typography
            variant="body2"
            sx={{ mt: 4, fontSize: 13, color: "#666666" }}
          >
            Need Help?{" "}
            <a
              href="tel:+918590422107"
              style={{
                background: "linear-gradient(180deg, #4AD16E 0%, #08B1B6 100%)",
                WebkitBackgroundClip: "text", // Clip background to text
                WebkitTextFillColor: "transparent",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              +91 85904 22107
            </a>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Powered by{" "}
            <a
              href="https://www.willsoar.in"
              style={{
                color: "#1C9478",
                textDecoration: "none",
                fontWeight: 500,
              }}
              target="_blank"
              rel="noreferrer"
            >
              Willsoar Technologies
            </a>
          </Typography>
        </LoginCard>
      </Background>
    </>
  );
});

export default LoginForm;